import { isRecord } from './typeGuards';
import { isFormDataLike } from './http';
import type { SchemaGenerationPayload } from '../types/generation';
import type { ModelConfigEntry } from '../config/models.config';

/**
 * 将模型默认配置（defaults）合并进 payload.api，仅在缺失时填充。
 */
export const applyModelDefaultsToPayload = (
  sourcePayload: SchemaGenerationPayload,
  modelConfig?: ModelConfigEntry | null
): SchemaGenerationPayload => {
  const defaults = modelConfig?.defaults;
  if (!isRecord(defaults) || Array.isArray(defaults)) {
    return sourcePayload;
  }

  const currentApi = isRecord(sourcePayload.api) ? sourcePayload.api : undefined;
  const mergedApi: Record<string, unknown> = currentApi ? { ...currentApi } : {};
  let mutated = false;

  for (const [key, value] of Object.entries(defaults)) {
    if (key === 'headers' && isRecord(value) && !Array.isArray(value)) {
      const existingHeaders = isRecord(mergedApi.headers) ? { ...mergedApi.headers } : {};
      let headerMutated = false;
      for (const [headerKey, headerValue] of Object.entries(value)) {
        if (existingHeaders[headerKey] === undefined) {
          existingHeaders[headerKey] = headerValue as unknown;
          headerMutated = true;
        }
      }
      if (headerMutated) {
        mergedApi.headers = existingHeaders;
        mutated = true;
      }
      continue;
    }

    if (mergedApi[key] === undefined) {
      mergedApi[key] = value as unknown;
      mutated = true;
    }
  }

  if (!mutated) {
    return sourcePayload;
  }

  return {
    ...sourcePayload,
    api: mergedApi as SchemaGenerationPayload['api'],
  };
};

/**
 * 从 payload 中提取 prompt 文本，兼容多种字段结构。
 */
export const extractPromptFromPayload = (value: SchemaGenerationPayload): string => {
  if (typeof value.prompt === 'string' && value.prompt.trim().length > 0) {
    return value.prompt;
  }
  if (typeof value.input === 'string' && value.input.trim().length > 0) {
    return value.input;
  }
  const { body } = value;
  if (body && !isFormDataLike(body) && typeof body === 'object') {
    const bodyRecord = body as Record<string, unknown>;
    const candidate = bodyRecord.prompt ?? bodyRecord.input;
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate;
    }
  }
  return '';
};

/**
 * 构建历史记录所需的参数快照，剔除内部字段。
 */
export const buildHistoryParameters = (payload: SchemaGenerationPayload): Record<string, unknown> => {
  const { meta, ...rest } = payload;
  const parameters: Record<string, unknown> = { ...rest };
  if (isRecord(meta)) {
    const metaCopy: Record<string, unknown> = { ...meta };
    delete metaCopy._tempTaskId;
    delete metaCopy._historyId;
    if (Object.keys(metaCopy).length > 0) {
      parameters.meta = metaCopy;
    }
  }
  return parameters;
};
